import { useEffect } from "react";
import { getBrowserSessionId, getBrowserTabId } from "utils/browserId";
import NoPII, { NoPIIString } from "utils/NoPII";
import gpuInfo from "utils/gpuInfo";
import { identifyUserForAppcues, trackEventForAppcues } from "./appcues";

import { UAParser } from "ua-parser-js";
import {
  ActionType,
  SurfaceType,
  AnalyticsProperties,
  AnalyticsSurface,
  OnboardingState,
  EventNameType,
} from "utils/analytics/analyticsConstants";
import uuid from "uuid-random";
import Cookies from "js-cookie";

import * as Sentry from "@sentry/browser";
import { envWithDefault, featureEnabled } from "./env";
import { ViewerQuery } from "graphql/queries/Viewer.generated";
import { assertExists } from "helpers/assertExists";
import storageInfo from "./storageInfo";

const parser = new UAParser(navigator.userAgent);

const SEGMENT_APP_BUILD = process.env.REACT_APP_SOURCE_VERSION;

export function setupAnalyticsMiddleware(
  analyticsLibrary: SegmentAnalytics.AnalyticsJS,
) {
  analyticsLibrary.addSourceMiddleware(function ({ payload, next }) {
    // Modify the payload object to add our own attributes, per documentation here:
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/middleware/

    // Obscure the user's IP
    payload.obj.context.ip = "0.0.0.0";

    // Add build identifier
    payload.obj.context.app = payload.obj.context.app || {};
    payload.obj.context.app.build = SEGMENT_APP_BUILD;

    const extraProps = payload.obj.traits || payload.obj.properties;
    if (extraProps) {
      extraProps.browserSessionId = getBrowserSessionId();
      extraProps.browserTabId = getBrowserTabId();
      // possible values: console, mobile, tablet, smarttv, wearable, embedded
      extraProps.platform = parser.getDevice().type;
      extraProps.os = parser.getOS().name;
    }

    // move along
    next(payload);
  });
  if (envWithDefault("CI", "false") === "true") {
    analyticsLibrary.addSourceMiddleware(function () {
      console.log("Not forwarding events to Segment in CI...");
    });
  }
}

type AnalyticsUserProperties = {
  id: string;
  email: string;
  type: string;
};

export function useIdentifyUser(currentUser?: AnalyticsUserProperties) {
  useEffect(() => {
    if (currentUser) {
      identifyUser(currentUser.id, currentUser.type, currentUser.email);
    }
  }, [currentUser]);
}

// Our contract with Segment includes a quota for a maximum number of tracked
// users per month. On non-production, we regularly generate new users with
// new publicIDs, which results in us running up the user count on our plan.
//
// Instead, on those tiers, we can consistently reassign users into a smaller
// ID space, which caps the quota impact of all our testing users.
//
// We also remove the prefix (e.g. 'Student-' or 'Teacher-') from the user ID.
// See comment in https://github.com/chanzuckerberg/along/pull/4335 for details.
export function analyticsId(userId: string) {
  let analyticsId = userId.split("-")[1];
  if (envWithDefault("ENABLE_LIMITED_SEGMENT_USERIDS") === "true") {
    analyticsId = analyticsId.substr(-2);
  }

  return analyticsId;
}

// The first time analytics.identify is called, it will preserve the existing anonymousId
// Subsequent calls with the same userId will preserve the existing anonymousId whereas
// calls with a different userId will change the anonymousId.
// When users log out (utils/logout.tsx), the userId will be set to null & anonymousId randomized
export async function identifyUser(
  userId: string, // Teacher-12355
  userType: string, // Teacher
  email: string,
) {
  // Note not passing analytics ID because the Appcues method calculates it
  identifyUserForAppcues(userId, userType);

  const analyticsUserId = analyticsId(userId);

  window.analytics.group(userType, {
    userId: analyticsUserId,
  });

  Sentry.setUser({
    id: analyticsUserId,
    email,
    type: userType,
  });

  window.analytics.identify(analyticsUserId, {
    userType: userType,
    isRealProductUser: checkRealUser(email),
    gpu: gpuInfo(),
    storage: await storageInfo(),
  });
}

function checkRealUser(email: string) {
  const testUserEmailRegex = /comealong\.[\w]*@gmail\.com/i;
  const testUserDomainRegex =
    /@(chanzuckerberg.com|hogwarts.edu|comealong.dev|along.dev|\w*\.*cohorts.comealong.dev|\w*\.*cohorts.along.dev|"example.\w*\.*)$/i;
  return !email.match(testUserEmailRegex) && !email.match(testUserDomainRegex);
}

export function resetUser() {
  // Sets the userId to be null, as well as randomly generates a new anonymousId.
  window.analytics.reset();
  // Make sure anonymous ID is appropriate if we're in a testing environment.
  setAnonymousId();
  Sentry.setUser({});
}

type PageProperties = {
  [key: string]: OnboardingState | string | undefined;
};
export function trackPageTransition(
  analyticsName: string,
  analyticsSurface: SurfaceType,
  properties?: PageProperties,
) {
  window.analytics.page(analyticsSurface, analyticsName, properties);
}

export function trackEvent(event: {
  eventName: EventNameType | string;
  eventType: ActionType;
  analyticsSurface: AnalyticsSurface;
  extraProperties: AnalyticsProperties;
}) {
  const convertedPropertyUserIds = convertPropertyUserIds(
    event.extraProperties,
  );
  window.analytics.track(event.eventName, {
    eventType: event.eventType,
    analyticsSurface: event.analyticsSurface,
    ...convertedPropertyUserIds,
  });
  trackEventForAppcues(NoPII(event.eventName), {
    eventType: event.eventType,
    analyticsSurface: event.analyticsSurface,
    extraMetadata: convertedPropertyUserIds.extraMetadata,
  });
}

type User = ViewerQuery["viewer"]["currentUser"];
export function getUserOnboardingState(
  currentUser: User,
): OnboardingState | undefined {
  if (currentUser.__typename !== "Teacher") {
    return undefined;
  }

  if (currentUser.hasActiveStudents) {
    return "does_have_students";
  }

  if (currentUser.canHaveStudents) {
    return "can_have_students";
  }

  switch (currentUser.emailVerificationStatus) {
    case "pending":
      return "email_is_pending";
    case "rejected":
      return "email_is_rejected";
    case "approved":
      return "email_is_verified";
  }
}

// TODO: Deprecate this check and apply prefix removal to all object IDs (coordinate with DE for backfill)
function isUserId(str: string) {
  return ["Student", "Teacher"].includes(str.split("-")[0]);
}

function removeIdPrefix(str: string) {
  return str.split("-")[1];
}

// user IDs may be sent to Segment as event properties.
// This ensures that they don't have model names appended to it
export function convertPropertyUserIds(
  properties: AnalyticsProperties,
): AnalyticsProperties {
  if (!featureEnabled("FEATURE_EXTERNAL_IDS")) {
    return properties;
  }

  if (
    typeof properties.studentId === "string" &&
    isUserId(properties.studentId)
  ) {
    properties.studentId = NoPII(removeIdPrefix(properties.studentId));
  }

  if (
    properties.sessionOwnerId != null &&
    isUserId(properties.sessionOwnerId)
  ) {
    properties.sessionOwnerId = removeIdPrefix(properties.sessionOwnerId);
  }

  const metadata = properties.extraMetadata;
  if (metadata) {
    Object.keys(metadata).forEach((key) => {
      const value = metadata[key];
      if (typeof value === "string" && isUserId(value)) {
        metadata[key] = NoPII(removeIdPrefix(value));
      } else if (
        Array.isArray(value) &&
        key !== "groupsUsageInfo" &&
        key !== "stickers"
      ) {
        metadata[key] = value.flatMap((prop) => {
          if (typeof prop === "string" && isUserId(prop)) {
            return removeIdPrefix(prop) as NoPIIString;
          }
          return prop;
        }) as NoPIIString[];
      }
    });
  }

  return properties;
}

// IMPORTANT: Unfortunately Segment doesn't have a mechanism to be notified when the anonymous id changes,
// so it's important that we're careful about where this function is invoked (because anyone using
// the anonymous ID (e.g. split.io SDK) won't get a notification that it changed).
let segmentAnonymousId: string;
function setAnonymousId() {
  // This is a bit janky. The Segment library should have a method to get the anonymous ID
  // that we could call here, but unfortunately they don't. If you want the gritty details:
  // There's a 'ready' event that we could subscribe to and then check
  // window.analytics.user().anonymousID() when it fires, but there's no guarantee the 'ready'
  // event will actually fire (yuck). Additionally, there's no reason for Segment to be blocking
  // access to anonymousID until the 'ready' event fires. I've reached out to their support team
  // and they agree, so hopefully this will get fixed in a future Segment release).
  //
  // Thankfully, it's trivial to just check cookies and local storage to see what the segment
  // anon ID  is and set it if needed. You can see how Segment does this (and generate an anon ID
  // if needed) here:
  // https://github.com/segmentio/analytics.js-core/blob/v4.1.11/lib/user.ts#L109-L154
  //
  // NOTE: Segment stores the anon ID as JSON (including a possible value of 'null').

  // Safari throws a SecurityError if you try to access local storage when blocking cookies.
  // https://michalzalecki.com/why-using-localStorage-directly-is-a-bad-idea/
  let localStorageId;
  try {
    localStorageId = window.localStorage?.getItem("ajs_anonymous_id");
  } catch {
    // Nothing to do
  }

  // Segment anonymous ids are uuids, which cant be parsed normally in JSON.
  // They need to be enclosed in a string. Therefore, add quotes around the
  // value if necessary before JSON.parsing.
  //
  // I'm not sure why this problem occurs, it seems like the quotes are always
  // included in these values when I test in the browser, but it costs very
  // little to do this additional work.
  //
  // TODO: Is the JSON parse even needed? Seems like special casing the null
  // case might be simpler? Otherwise we're just going from string to string.
  // Not trying to figure this out right now, that's going too deep.
  let parseValue = Cookies.get("ajs_anonymous_id") || localStorageId || "null";
  if (parseValue !== "null" && parseValue[0] !== '"') {
    parseValue = `"${parseValue}"`;
  }
  const idFromSegment = JSON.parse(parseValue);

  segmentAnonymousId = idFromSegment || uuid();

  // In non-production tiers, we want to limit the effect of our testing events on our
  // Segment user quota. Here, we limit the anonymousIds to a small range.
  if (
    featureEnabled("ENABLE_LIMITED_SEGMENT_USERIDS") &&
    !segmentAnonymousId.includes("anonymous-along-testing-id")
  ) {
    const random = Math.floor(Math.random() * 100);
    segmentAnonymousId = `anonymous-along-testing-id-${random}`;
  }

  window.analytics.setAnonymousId(segmentAnonymousId);
}

// Initializes the segment anonymous ID (including some logic to ensure we don't use too many
// segment IDs in our test environment). Intended to be called once when the app is initialized.
export function initializeSegmentAnonymousId() {
  if (segmentAnonymousId) {
    return;
  }

  setAnonymousId();
}

export function getSegmentAnonymousId() {
  assertExists(segmentAnonymousId);

  // Prefer to get ID directly from segment if possible (since it's the source of truth).
  // It should be very rare that there's a mismatch here.
  return window.analytics.user
    ? window.analytics.user().anonymousId()
    : segmentAnonymousId;
}
